import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useInfiniteQuery } from "@tanstack/react-query";
import {
  Box,
  Button,
  MenuItem,
  Skeleton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import FilterListIcon from "@mui/icons-material/FilterList";
import DownloadIcon from "@mui/icons-material/Download";

import {
  downloadRiskAssessmentHistoryExport,
  getRiskAssessmentHistory,
} from "../../../../shared/api";
import { useClient } from "../../../../contexts/ClientContext";
import {
  buildHistoryParams,
  DEFAULT_HISTORY_FILTERS,
  DEFAULT_HISTORY_ORDER,
  hasValidCoordinates,
  toMarkerInfo,
} from "../../history/helpers";
import { MAP_STYLE } from "../../constants";
import AssessmentCard from "./AssessmentCard";
import HistorySearchField from "../../../shared/HistorySearchField";
import HistoryFiltersModal from "./HistoryFiltersModal";
import { useAssessmentPdfExport } from "./useAssessmentPdfExport";

const PAGE_SIZE = 20;

function HistoryTabView({
  mapRef,
  setMarkerInfo,
  active,
  setHistoryAssessmentsForMap,
  selectedEarthquakeLayers,
}) {
  const { t } = useTranslation();
  const { clientInfo } = useClient();
  const loaderRef = useRef(null);

  const [orderCombined, setOrderCombined] = useState(DEFAULT_HISTORY_ORDER);
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [filters, setFilters] = useState(DEFAULT_HISTORY_FILTERS);
  const [filtersModalOpen, setFiltersModalOpen] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const {
    exportAssessmentPdf,
    activeExportKey,
    errorMessage: pdfErrorMessage,
    clearError: clearPdfError,
  } = useAssessmentPdfExport();

  const commonParams = useMemo(
    () =>
      buildHistoryParams({
        search: debouncedSearch,
        orderCombined,
        filters,
      }),
    [debouncedSearch, filters, orderCombined],
  );

  const fetchPage = useCallback(
    async ({ pageParam = 1 }) => {
      const response = await getRiskAssessmentHistory({
        clientId: clientInfo.clientId,
        ...commonParams,
        page: pageParam,
        limit: PAGE_SIZE,
      });

      if (Array.isArray(response)) {
        return {
          items: response,
          page: pageParam,
          totalPages: 1,
          total: response.length,
        };
      }

      return {
        items: response.items ?? [],
        page: response.page ?? pageParam,
        totalPages: response.totalPages ?? 1,
        total: response.total ?? 0,
      };
    },
    [clientInfo?.clientId, commonParams],
  );

  const {
    data,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    refetch,
    error,
  } = useInfiniteQuery({
    queryKey: ["riskAssessmentHistory", clientInfo?.clientId, commonParams],
    queryFn: fetchPage,
    initialPageParam: 1,
    getNextPageParam: (lastPage) => {
      const { page, totalPages } = lastPage;
      return page < totalPages ? page + 1 : undefined;
    },
    enabled: !!clientInfo?.clientId && active,
  });

  useEffect(() => {
    if (!loaderRef.current) return;
    if (!hasNextPage || isFetchingNextPage) return;

    const node = loaderRef.current;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasNextPage && !isFetchingNextPage) {
          fetchNextPage();
        }
      },
      { root: null, rootMargin: "200px", threshold: 0 },
    );
    observer.observe(node);
    return () => observer.unobserve(node);
  }, [fetchNextPage, hasNextPage, isFetchingNextPage]);

  const items = useMemo(
    () => (data?.pages ?? []).flatMap((page) => page.items ?? []),
    [data],
  );

  const totalCount = useMemo(() => data?.pages?.[0]?.total ?? 0, [data]);

  const handleAssessmentClick = useCallback(
    (assessment) => {
      if (!hasValidCoordinates(assessment)) return;

      const markerInfo = toMarkerInfo(assessment);
      if (!markerInfo) return;

      const map = mapRef?.current?.getMap?.();
      if (map) {
        map.flyTo({
          center: [markerInfo.riskData.longitude, markerInfo.riskData.latitude],
          zoom: 15,
          essential: true,
        });
      }

      setMarkerInfo(markerInfo);
    },
    [mapRef, setMarkerInfo],
  );

  const handleAssessmentPdfDownload = useCallback(
    async (assessment) => {
      clearPdfError();
      const map = mapRef?.current?.getMap?.();
      const currentSprite = map?.getStyle?.()?.sprite || "";
      const currentMapStyle = currentSprite.includes("satellite")
        ? MAP_STYLE.satellite
        : MAP_STYLE.street;
      await exportAssessmentPdf({
        assessment,
        mapStyle: currentMapStyle,
        earthquakeLayers: selectedEarthquakeLayers,
        scopeKey: assessment?.id || "history-assessment",
      });
    },
    [clearPdfError, exportAssessmentPdf, mapRef, selectedEarthquakeLayers],
  );

  const orderOptions = useMemo(
    () => [
      {
        value: "creation_time:desc",
        label: t("risksMap.history.sort.newest"),
      },
      {
        value: "creation_time:asc",
        label: t("risksMap.history.sort.oldest"),
      },
      {
        value: "fire:desc",
        label: t("risksMap.history.sort.fireDesc"),
      },
      {
        value: "fire:asc",
        label: t("risksMap.history.sort.fireAsc"),
      },
      {
        value: "flood:desc",
        label: t("risksMap.history.sort.floodDesc"),
      },
      {
        value: "flood:asc",
        label: t("risksMap.history.sort.floodAsc"),
      },
      {
        value: "earthquake:desc",
        label: t("risksMap.history.sort.earthquakeDesc"),
      },
      {
        value: "earthquake:asc",
        label: t("risksMap.history.sort.earthquakeAsc"),
      },
    ],
    [t],
  );

  const activeFiltersCount = useMemo(() => {
    let count = 0;
    if (filters.creationTimeStart) count += 1;
    if (filters.creationTimeEnd) count += 1;
    if (filters.fireRiskRange[0] > 0 || filters.fireRiskRange[1] < 1) count += 1;
    if (filters.floodRiskRange[0] > 0 || filters.floodRiskRange[1] < 1) count += 1;
    if (
      filters.earthquakeRiskRange[0] > 0 ||
      filters.earthquakeRiskRange[1] < 1
    ) {
      count += 1;
    }
    return count;
  }, [filters]);

  const handleExport = useCallback(async () => {
    if (!clientInfo?.clientId || isExporting) return;

    setIsExporting(true);
    try {
      await downloadRiskAssessmentHistoryExport({
        clientId: clientInfo.clientId,
        ...commonParams,
      });
    } catch (exportError) {
      console.error("Failed to export risk assessments:", exportError);
      alert(t("risksMap.history.errors.exportFailed"));
    } finally {
      setIsExporting(false);
    }
  }, [clientInfo?.clientId, commonParams, isExporting, t]);

  useEffect(() => {
    if (!active || !clientInfo?.clientId) {
      setHistoryAssessmentsForMap([]);
      return;
    }

    let isCancelled = false;

    const fetchAllAssessmentsForMap = async () => {
      try {
        const firstResponse = await getRiskAssessmentHistory({
          clientId: clientInfo.clientId,
          ...commonParams,
          page: 1,
        });

        let mergedItems = Array.isArray(firstResponse)
          ? firstResponse
          : firstResponse.items || [];

        if (!Array.isArray(firstResponse) && firstResponse.totalPages > 1) {
          for (let page = 2; page <= firstResponse.totalPages; page += 1) {
            const pageResponse = await getRiskAssessmentHistory({
              clientId: clientInfo.clientId,
              ...commonParams,
              page,
            });
            const nextItems = Array.isArray(pageResponse)
              ? pageResponse
              : pageResponse.items || [];
            mergedItems = mergedItems.concat(nextItems);
          }
        }

        const validItems = mergedItems.filter(hasValidCoordinates);

        if (!isCancelled) {
          setHistoryAssessmentsForMap(validItems);
        }
      } catch (err) {
        if (!isCancelled) {
          setHistoryAssessmentsForMap([]);
        }
      }
    };

    fetchAllAssessmentsForMap();

    return () => {
      isCancelled = true;
    };
  }, [active, clientInfo?.clientId, commonParams, setHistoryAssessmentsForMap]);

  if (!active) return null;

  return (
    <>
      <Stack direction="column" spacing={2} sx={{ mb: 2 }}>
        <HistorySearchField
          placeholder={t("risksMap.history.searchPlaceholder")}
          onDebouncedChange={setDebouncedSearch}
        />

        <Stack direction="row" spacing={1} alignItems="center">
          <TextField
            select
            fullWidth
            size="small"
            label={t("common.order")}
            value={orderCombined}
            onChange={(event) => setOrderCombined(event.target.value)}
          >
            {orderOptions.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
          <Button
            variant="outlined"
            startIcon={<FilterListIcon />}
            sx={{ minWidth: "130px" }}
            onClick={() => setFiltersModalOpen(true)}
          >
            {t("portfolioMap.filters")}
            {activeFiltersCount > 0 && ` (${activeFiltersCount})`}
          </Button>
        </Stack>
      </Stack>

      {!isLoading && totalCount > 0 && (
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{ mb: 1 }}
        >
          <Typography variant="body2" color="text.secondary">
            {t("portfolioMap.resultsCount", { count: totalCount })}
          </Typography>
          <Tooltip title={t("risksMap.history.exportTooltip")}>
            <span>
              <Button
                variant="text"
                size="small"
                startIcon={<DownloadIcon />}
                onClick={handleExport}
                disabled={isExporting}
              >
                {isExporting
                  ? t("risksMap.history.exporting")
                  : t("risksMap.history.export")}
              </Button>
            </span>
          </Tooltip>
        </Stack>
      )}

      {pdfErrorMessage ? (
        <Typography color="error" variant="body2" sx={{ mb: 1 }}>
          {pdfErrorMessage}
        </Typography>
      ) : null}

      {error ? (
        <Stack gap={1}>
          <Typography color="error">
            {error?.error_code === "permissions_unavailable"
              ? t("auth.errors.permissionsUnavailable")
              : error?.status === 403 || error?.error_code === "missing_permission"
              ? t("auth.accessDenied")
              : t("risksMap.history.errors.loadFailed")}
          </Typography>
          <Button variant="contained" onClick={() => refetch()}>
            {t("common.tryAgain")}
          </Button>
        </Stack>
      ) : (
        <>
          <Stack gap={2}>
            {isLoading &&
              Array.from({ length: 6 }).map((_, index) => (
                <Box key={`history-loading-${index}`} p={1} borderRadius={2} border="1px solid #eee">
                  <Skeleton variant="text" width="55%" />
                  <Skeleton variant="text" width="70%" />
                  <Skeleton variant="rectangular" height={56} />
                </Box>
              ))}

            {items.map((assessment, index) => (
              <Box key={assessment.id || `assessment-${index}`}>
                <AssessmentCard
                  assessment={assessment}
                  onClick={handleAssessmentClick}
                  onDownloadPdf={handleAssessmentPdfDownload}
                  isDownloadingPdf={activeExportKey === (assessment?.id || "history-assessment")}
                />
              </Box>
            ))}

            {isFetchingNextPage &&
              Array.from({ length: 4 }).map((_, index) => (
                <Box key={`history-loading-next-${index}`} p={1} borderRadius={2} border="1px solid #eee">
                  <Skeleton variant="text" width="55%" />
                  <Skeleton variant="text" width="70%" />
                  <Skeleton variant="rectangular" height={56} />
                </Box>
              ))}

            {!isLoading && items.length === 0 && (
              <Typography variant="body2" color="text.secondary">
                {t("risksMap.history.emptyForMember")}
              </Typography>
            )}
          </Stack>

          {hasNextPage && <Box ref={loaderRef} sx={{ height: 1 }} />}
        </>
      )}

      <HistoryFiltersModal
        open={filtersModalOpen}
        onClose={() => setFiltersModalOpen(false)}
        filters={filters}
        onFiltersChange={setFilters}
      />
    </>
  );
}

export default HistoryTabView;
